import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Send, Users } from "lucide-react";
import ChatBubble from "@/components/ChatBubble";
import { MOCK_MESSAGES, MOCK_CHATS } from "@/lib/mockData";
import { useAuth } from "@/lib/AuthContext";
import { apiChats, wsUrl } from "@/lib/api";

const ChatDetail = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const fallback = MOCK_CHATS.find((c) => c.id === id) || MOCK_CHATS[0];

  const [chatTitle, setChatTitle] = useState(fallback.title);
  const [messages, setMessages] = useState(user ? [] : MOCK_MESSAGES);
  const [text, setText] = useState("");
  const [typing, setTyping] = useState(null);
  const endRef = useRef(null);
  const wsRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    apiChats.messages(id).then((m) => {
      setMessages(m || []);
      // Title comes from chat list lookup
      apiChats.list().then((list) => {
        const c = (list || []).find((x) => x.id === id);
        if (c) setChatTitle(c.title);
      }).catch(() => {});
    }).catch(() => {});

    // Open websocket
    try {
      const ws = new WebSocket(wsUrl(id));
      wsRef.current = ws;
      ws.onmessage = (ev) => {
        try {
          const p = JSON.parse(ev.data);
          if (p.type === "message") {
            setMessages((prev) => [...prev, { ...p.message, me: p.message.sender_id === user.user_id }]);
          } else if (p.type === "typing" && p.user !== user.name) {
            setTyping(p.user);
            setTimeout(() => setTyping(null), 3000);
          }
        } catch {}
      };
      return () => { try { ws.close(); } catch {} };
    } catch {}
  }, [id, user]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = () => {
    if (!text.trim()) return;
    if (user && wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "message", text }));
    } else {
      // Optimistic fallback (mock mode)
      setMessages((prev) => [...prev, { id: `m_${Date.now()}`, sender: user || { name: "You" }, text, iso: new Date().toISOString(), me: true }]);
    }
    setText("");
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex flex-col" data-testid="chat-detail-page">
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b-2 border-[#0A0A0A]">
        <div className="max-w-2xl mx-auto px-4 h-16 flex items-center gap-3">
          <button onClick={() => nav(-1)} className="w-10 h-10 brutal-border rounded-full bg-white flex items-center justify-center brutal-shadow-sm" data-testid="chat-back">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="font-heading font-bold text-base truncate">{chatTitle}</h1>
            <div className="text-xs text-[#555555] flex items-center gap-1">
              <Users className="w-3 h-3" /> Group chat
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-2xl mx-auto w-full px-4 py-4 overflow-y-auto pb-32">
        {messages.length === 0 && (
          <div className="text-center text-sm text-[#555] py-10">No messages yet. Say g&apos;day.</div>
        )}
        {messages.map((m) => (
          <ChatBubble key={m.id} message={{ ...m, sender: m.sender || { name: "Unknown", avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=120" } }} />
        ))}
        {typing && (
          <div className="flex items-center gap-2 ml-9 text-xs text-[#555555]">
            <div className="flex gap-0.5">
              <span className="w-1.5 h-1.5 bg-[#555555] rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
              <span className="w-1.5 h-1.5 bg-[#555555] rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
              <span className="w-1.5 h-1.5 bg-[#555555] rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
            </div>
            {typing} is typing…
          </div>
        )}
        <div ref={endRef} />
      </main>

      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t-2 border-[#0A0A0A] px-4 py-3 z-30">
        <div className="max-w-2xl mx-auto flex gap-2">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Type a message"
            className="brutal-input flex-1"
            data-testid="chat-message-input"
          />
          <button onClick={send} className="brutal-btn bg-[#FF6A2C] text-white px-4" data-testid="chat-send-btn">
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatDetail;
